<div class="expenses-container" [class.ce--wide]="expanded">
  <div class="custom-mat-spinner" *ngIf="isSpinnerShown">
    <mat-spinner></mat-spinner>
    <div class="text">Data loading...</div>
  </div>

  <ng-container *ngIf="!isSpinnerShown">
    <!-- See details: the panel grows to the left, the spending charted beside the full list. -->
    <aside class="ce-insights" *ngIf="expanded" aria-label="Spending">
      <div class="ce-insights__head">
        <h3>Last 12 months</h3>
        <span class="ce-insights__sub">Bills and expenses by month, less credit notes</span>
      </div>

      <div class="ce-kpis">
        <div class="ce-kpi">
          <span class="ce-kpi__label">Spent</span>
          <span class="ce-kpi__value num">${{ spend12 | number: '1.0-0' }}</span>
          <span class="ce-kpi__sub" *ngIf="creditTotal12">after ${{ creditTotal12 | number: '1.0-0' }} credits</span>
        </div>
        <div class="ce-kpi">
          <span class="ce-kpi__label">Monthly average</span>
          <span class="ce-kpi__value num">${{ monthlyAverage | number: '1.0-0' }}</span>
        </div>
        <div class="ce-kpi">
          <span class="ce-kpi__label">Average bill</span>
          <span class="ce-kpi__value num">${{ averageBill | number: '1.0-0' }}</span>
          <span class="ce-kpi__sub">{{ billCount12 }} {{ billCount12 === 1 ? 'bill' : 'bills' }}</span>
        </div>
        <div class="ce-kpi">
          <span class="ce-kpi__label">Last paid</span>
          <span class="ce-kpi__value">{{ lastPaid || '—' }}</span>
        </div>
      </div>

      <div class="ce-chart" role="img" [attr.aria-label]="'Spend by month over the last 12 months, $' + (spend12 | number: '1.0-0') + ' in all'">
        <div class="ce-chart__bars">
          <div class="ce-chart__col" *ngFor="let m of months" [title]="monthTitle(m)">
            <div class="ce-chart__track">
              <!-- The month's value above its bar, so the chart reads without hovering. -->
              <span class="ce-chart__value num" [style.bottom.%]="barHeight(m.bills + m.payments)">{{ shortMoney(m.bills + m.payments) }}</span>
              <div class="ce-chart__stack" [style.height.%]="barHeight(m.bills + m.payments)">
                <span class="ce-chart__seg ce-chart__seg--payments" [style.flex-grow]="m.payments"></span>
                <span class="ce-chart__seg ce-chart__seg--bills" [style.flex-grow]="m.bills"></span>
              </div>
            </div>
            <span class="ce-chart__label">{{ m.label }}</span>
          </div>
        </div>
        <div class="ce-chart__legend">
          <span><i class="ce-dot ce-dot--bills"></i>Bills</span>
          <span><i class="ce-dot ce-dot--payments"></i>Expenses</span>
        </div>
      </div>
    </aside>

    <div class="ce-main">
      <!-- Snapshot -->
      <div class="snapshot">
        <div class="snap-card">
          <div class="snap-label">Outstanding</div>
          <div class="snap-big num">${{ outstandingTotal | number: '1.2-2' }}</div>
          <div class="snap-sub">{{ openBillsCount }} open {{ openBillsCount === 1 ? 'bill' : 'bills' }}</div>
        </div>
        <div class="snap-card">
          <div class="snap-label">Overdue</div>
          <div class="snap-big num danger">${{ overdueTotal | number: '1.2-2' }}</div>
          <div class="snap-sub">{{ overdueCount }} {{ overdueCount === 1 ? 'bill' : 'bills' }}</div>
        </div>
      </div>

      <!-- The narrow panel: what it adds up to, and the latest few. -->
      <ng-container *ngIf="!expanded">
        <div class="ce-summary" *ngIf="expenses.length">
          <span><strong class="num">${{ spend12 | number: '1.0-0' }}</strong> spent in the last 12 months</span>
          <span class="ce-summary__sep">·</span>
          <span><strong class="num">${{ monthlyAverage | number: '1.0-0' }}</strong> a month on average</span>
        </div>

        <div class="ce-section-head">
          <h3>Recent activity</h3>
          <span class="ce-section-head__count">{{ counts.all }} in all</span>
        </div>

        <div class="txtable">
          <div class="placeholder" *ngIf="!expenses.length">No transactions yet</div>
          <ng-container *ngFor="let e of recentExpenses">
            <ng-container *ngTemplateOutlet="row; context: { $implicit: e }"></ng-container>
          </ng-container>
        </div>

        <button type="button" class="ce-see-details" *ngIf="expenses.length" (click)="toggleExpanded()">
          See details
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M6.5 3H3v3.5M3 3l4 4M9.5 13H13V9.5M13 13l-4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </ng-container>

      <!-- See details: every transaction, by type and search. -->
      <ng-container *ngIf="expanded">
        <div class="ce-toolbar">
          <div class="ce-type">
            <button type="button" class="ce-type__pill" [class.ce-type__pill--on]="activeFilter !== 'all'"
                    (click)="typeMenuOpen = !typeMenuOpen" [attr.aria-expanded]="typeMenuOpen" aria-haspopup="listbox">
              {{ activeFilterLabel }}
              <span class="ce-type__count">{{ counts[activeFilter] }}</span>
              <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 4.5L6 7.5l3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="ce-type__back" *ngIf="typeMenuOpen" (click)="typeMenuOpen = false"></div>
            <ul class="ce-type__menu" *ngIf="typeMenuOpen" role="listbox" aria-label="Type">
              <li *ngFor="let f of filters" role="option" [attr.aria-selected]="activeFilter === f.id"
                  [class.is-on]="activeFilter === f.id" (click)="setFilter(f.id)">
                <span>{{ f.id === 'all' ? 'All types' : f.label }}</span>
                <span class="ce-type__count">{{ counts[f.id] }}</span>
              </li>
            </ul>
          </div>

          <!-- A div, not a label: hosts in Settings › Lists turn every .form-group label into a centred block. -->
          <div class="ce-search">
            <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor"
                 stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="9" cy="9" r="5.2"></circle>
              <path d="M13 13l3.5 3.5"></path>
            </svg>
            <input class="ce-search__input" type="search" [(ngModel)]="searchQuery"
                   placeholder="Search amount, reference or number" aria-label="Search transactions" />
          </div>

          <button type="button" class="ce-expand" (click)="toggleExpanded()" title="Back to the narrow panel">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M7 3.5V7H3.5M7 7L3 3M9 12.5V9h3.5M9 9l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Collapse
          </button>
        </div>

        <div class="txtable">
          <div class="placeholder" *ngIf="filteredExpenses.length === 0">No transactions match</div>
          <ng-container *ngFor="let e of filteredExpenses">
            <ng-container *ngTemplateOutlet="row; context: { $implicit: e }"></ng-container>
          </ng-container>
        </div>

        <div class="bill-total" *ngIf="filteredExpenses.length > 0">
          <span class="lbl">Total outstanding</span>
          <span class="val num">${{ outstandingTotal | number: '1.2-2' }}</span>
        </div>
      </ng-container>
    </div>
  </ng-container>
</div>

<!-- One transaction, the same in Recent activity and the full list. -->
<ng-template #row let-e>
  <div class="txrow" [class.clickable]="kindOf(e) === 'bill'" (click)="openExpense(e)">
    <div class="tx-type">
      <span class="tx-ticon" [ngClass]="kindOf(e)">
        <svg *ngIf="kindOf(e) === 'bill'" viewBox="0 0 20 20" width="15" height="15" fill="none"
             stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 3h8l2 2v12l-2-1.3-1.5 1.3L10 16l-1.5 1.3L7 16l-2 1.3V3z"></path>
        </svg>
        <svg *ngIf="kindOf(e) === 'payment'" viewBox="0 0 20 20" width="15" height="15" fill="none"
             stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 7h14M3 7v8h14V7M3 7l2-3h10l2 3M8 12h4"></path>
        </svg>
        <svg *ngIf="kindOf(e) === 'credit'" viewBox="0 0 20 20" width="15" height="15" fill="none"
             stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10 16V5M5 10l5-5 5 5"></path>
        </svg>
      </span>
      <div class="tx-main">
        <div class="tx-tlabel">
          {{ kindLabel(e) }}
          <span class="tx-number num" *ngIf="e.number">· {{ e.number }}</span>
        </div>
        <div class="tx-meta">{{ metaLine(e) }}</div>
      </div>
    </div>
    <div class="tx-right">
      <span class="tx-amt num" [class.neg]="signedTotal(e) < 0">
        {{ signedTotal(e) < 0 ? '-' : '' }}${{ absTotal(e) | number: '1.2-2' }}
      </span>
      <span class="badge" [ngClass]="displayStatus(e)">{{ statusLabel(e) }}</span>
    </div>
  </div>
</ng-template>
